import { useEffect, useRef } from 'react';

import { trackEventMixpanel } from 'utils/mixpanel';
import { getWhitespaceEntryPoint, getWhitespaceScrollDepthPct, type computeWhitespaceGridMetrics } from './whitespaceMixpanel.helpers';
import { PPA_WHITESPACE_CHART_LAZY_LOAD_TRIGGERED, PPA_WHITESPACE_DATA_LOAD_SUCCEEDED, 
    PPA_WHITESPACE_OVERLAY_RENDERED, PPA_WHITESPACE_SESSION_TIME_SPENT, PPA_WHITESPACE_VIEWED } from 'utils/mixpanelEventNames';

type GridMetrics = ReturnType<typeof computeWhitespaceGridMetrics>;

type UseWhitespaceMixpanelEffectsParams = {
    isMetaLoading: boolean;
    isGridFetched: boolean;
    isGridFetching: boolean;
    xAxis: string;
    yAxis: string;
    crossSection: string;
    showWhitespaces: boolean;
    columnCount: number;
    rowCount: number;
    totalColumnCount: number;
    totalRowCount: number;
    isFetchingMoreRows: boolean;
    isFetchingMoreColumns: boolean;
    gridMetrics: GridMetrics;
    globalFiltersApplied: string;
    showNoFilterSelection: boolean;
};

const useWhitespaceMixpanelEffects = ({
    isMetaLoading,
    isGridFetched,
    isGridFetching,
    xAxis,
    yAxis,
    crossSection,
    showWhitespaces,
    columnCount,
    rowCount,
    totalColumnCount,
    totalRowCount,
    isFetchingMoreRows,
    isFetchingMoreColumns,
    gridMetrics,
    globalFiltersApplied,
    showNoFilterSelection,
}: UseWhitespaceMixpanelEffectsParams) => {
    const hasTrackedPageViewRef = useRef(false);
    const hasTrackedDataLoadRef = useRef(false);
    const previousShowWhitespacesRef = useRef(showWhitespaces);
    const previousLoadedCountsRef = useRef({ columns: 0, rows: 0 });
    const wasFetchingMoreRef = useRef(false);
    const pageLoadStartedAtRef = useRef(performance.now());
    const sessionStartedAtRef = useRef(Date.now());

    useEffect(() => {
        if (isMetaLoading || hasTrackedPageViewRef.current) return;
        const hasAppliedFilters = globalFiltersApplied !== '-';
        if (!hasAppliedFilters && !showNoFilterSelection) return;

        hasTrackedPageViewRef.current = true;
        trackEventMixpanel(PPA_WHITESPACE_VIEWED, {
            entry_point: getWhitespaceEntryPoint(),
            global_filters_applied: globalFiltersApplied,
        });
    }, [globalFiltersApplied, isMetaLoading, showNoFilterSelection]);

    useEffect(() => {
        const sessionStartedAt = sessionStartedAtRef.current;
        return () => {
            trackEventMixpanel(
                PPA_WHITESPACE_SESSION_TIME_SPENT,
                { session_duration_seconds: Math.round((Date.now() - sessionStartedAt) / 1000) },
                { keepalive: true },
            );
        };
    }, []);

    useEffect(() => {
        if (!isGridFetched || isGridFetching || hasTrackedDataLoadRef.current) return;
        hasTrackedDataLoadRef.current = true;
        trackEventMixpanel(PPA_WHITESPACE_DATA_LOAD_SUCCEEDED, {
            x_axis: xAxis,
            y_axis: yAxis,
            cross_section: crossSection,
            render_time_ms: Math.round(performance.now() - pageLoadStartedAtRef.current),
        });
    }, [crossSection, isGridFetched, isGridFetching, xAxis, yAxis]);

    useEffect(() => {
        if (isGridFetching) {
            previousLoadedCountsRef.current = { columns: 0, rows: 0 };
            wasFetchingMoreRef.current = false;
            return;
        }

        const isFetchingMore = isFetchingMoreRows || isFetchingMoreColumns;
        const finishedLazyLoad = wasFetchingMoreRef.current && !isFetchingMore;
        wasFetchingMoreRef.current = isFetchingMore;

        if (!isGridFetched || isFetchingMore) return;

        const previous = previousLoadedCountsRef.current;
        const didLoadMoreColumns = columnCount > previous.columns;
        const didLoadMoreRows = rowCount > previous.rows;
        previousLoadedCountsRef.current = { columns: columnCount, rows: rowCount };

        if (!finishedLazyLoad || (!didLoadMoreColumns && !didLoadMoreRows)) return;

        const resolvedTotalColumns = Math.max(totalColumnCount, columnCount);
        const resolvedTotalRows = Math.max(totalRowCount, rowCount);

        trackEventMixpanel(PPA_WHITESPACE_CHART_LAZY_LOAD_TRIGGERED, {
            columns_loaded: columnCount,
            total_columns: resolvedTotalColumns,
            columns_scroll_depth_pct: getWhitespaceScrollDepthPct(columnCount, resolvedTotalColumns),
            rows_loaded: rowCount,
            total_rows: resolvedTotalRows,
            rows_scroll_depth_pct: getWhitespaceScrollDepthPct(rowCount, resolvedTotalRows),
        });
    }, [
        columnCount,
        isFetchingMoreColumns,
        isFetchingMoreRows,
        isGridFetched,
        isGridFetching,
        rowCount,
        totalColumnCount,
        totalRowCount,
    ]);

    useEffect(() => {
        if (previousShowWhitespacesRef.current === showWhitespaces) return;
        previousShowWhitespacesRef.current = showWhitespaces;
        if (!showWhitespaces) return;

        trackEventMixpanel(PPA_WHITESPACE_OVERLAY_RENDERED, {
            x_axis: xAxis,
            y_axis: yAxis,
            cross_section: crossSection,
            opportunity_cell_count: gridMetrics.opportunity_cell_count,
        });
    }, [crossSection, gridMetrics.opportunity_cell_count, showWhitespaces, xAxis, yAxis]);
};

export default useWhitespaceMixpanelEffects;
